import React from 'react';
import { SearchIcon, SlidersHorizontalIcon, ChevronDownIcon, HeartHandshakeIcon, UserIcon, UsersIcon, BookOpenIcon, MapPinIcon, AlertCircleIcon, LayoutGridIcon, HeartIcon, FlameIcon, ZapIcon, BookmarkIcon, LinkIcon, GlobeIcon } from 'lucide-react';
import { SearchWithAutocomplete, Suggestion } from '../UI/SearchWithAutocomplete';
interface ContentPageHeaderProps {
  title: string;
  icon: React.ReactNode;
  searchPlaceholder: string;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  suggestions?: Suggestion[];
  onSuggestionSelect?: (suggestion: Suggestion) => void;
}
const ContentPageHeader: React.FC<ContentPageHeaderProps> = ({
  title,
  icon,
  searchPlaceholder,
  searchQuery,
  setSearchQuery,
  showFilters,
  setShowFilters,
  suggestions = [],
  onSuggestionSelect
}) => {
  return <div className="max-w-7xl mx-auto px-4 py-8" data-id="element-1271" data-mp="17gu8xhlx8awg">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-1272" data-mp="17hm5ovr98eob">
        <div className="flex items-center gap-2" data-id="element-1273" data-mp="1hiqxt0wti8w8">
          {icon}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-id="element-1274" data-mp="1cpfqkyo3fq3s">{title}</h1>
        </div>
        {/* Search Bar */}
        <div className="relative flex-grow max-w-md" data-id="element-1275" data-mp="17orysas1j58i">
          {suggestions.length > 0 ? <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} suggestions={suggestions} placeholder={searchPlaceholder} onSelect={onSuggestionSelect} variant="rounded" data-id="element-1276" data-mp="12px86mnu0cdu" /> : <div className="relative" data-id="element-1277" data-mp="1qjp0nfvisad8">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1278" data-mp="1ljau629kh8hb">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1279" data-mp="f2pd0xk4ajpj" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder={searchPlaceholder} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1280" data-mp="a3m5n6go01ee" />
            </div>}
        </div>
        {/* Filter Toggle Button — mobile only, desktop sidebar is always visible */}
        <div className="flex items-center gap-3 lg:hidden" data-id="element-1281" data-mp="2ejr6ain24fai">
          <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-300 font-medium" data-id="element-1282" data-mp="2jck5kqzravf7">
            <SlidersHorizontalIcon className="h-5 w-5" data-id="element-1283" data-mp="2aki891kzhv95" />
            Filters
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} data-id="element-1284" data-mp="1vm6eb2ozx0lo" />
          </button>
        </div>
      </div>
    </div>;
};
// Helper function to get the appropriate icon for each content page
export const getContentPageIcon = (pageType: string) => {
  switch (pageType.toLowerCase()) {
    case 'tropes':
      return <HeartHandshakeIcon className="h-6 w-6 text-indigo-500" data-id="element-1285" data-mp="1qygevv1g1aqo" />;
    case 'characters':
      return <UserIcon className="h-6 w-6 text-indigo-500" data-id="element-1286" data-mp="1lz3stciec7p5" />;
    case 'relationshiptypes':
      return <UsersIcon className="h-6 w-6 text-indigo-500" data-id="element-1287" data-mp="1zylkbzn2tz27" />;
    case 'pairingtypes':
      return <LinkIcon className="h-6 w-6 text-pink-500" data-id="element-1288" data-mp="1zw8wj5un0uh8" />;
    case 'spicelevels':
    case 'spicedarkness':
      return <FlameIcon className="h-6 w-6 text-indigo-500" data-id="element-1289" data-mp="29oewb1r4sg6z" />;
    case 'burnlevels':
      return <ZapIcon className="h-6 w-6 text-amber-500" data-id="element-1290" data-mp="29q99ftw5v40i" />;
    case 'endingtypes':
      return <BookmarkIcon className="h-6 w-6 text-indigo-500" data-id="element-1291" data-mp="24q3mgr0u65or" />;
    case 'representation':
      return <UsersIcon className="h-6 w-6 text-indigo-500" data-id="element-1292" data-mp="2jn48qf09d8fu" />;
    case 'culturalidentity':
      return <GlobeIcon className="h-6 w-6 text-indigo-500" data-id="element-1293" data-mp="2ep2o3pn6p129" />;
    case 'themes':
      return <BookOpenIcon className="h-6 w-6 text-indigo-500" data-id="element-1294" data-mp="bzt0llgr2tw1" />;
    case 'settings':
      return <MapPinIcon className="h-6 w-6 text-indigo-500" data-id="element-1295" data-mp="gz5l3zk5fovg" />;
    case 'contentwarnings':
      return <AlertCircleIcon className="h-6 w-6 text-indigo-500" data-id="element-1296" data-mp="lq4456wiv12e" />;
    case 'viewall':
    case 'allcontenttags':
      return <LayoutGridIcon className="h-6 w-6 text-indigo-500" data-id="element-1297" data-mp="qq0b9f0in8m6" />;
    default:
      return <BookOpenIcon className="h-6 w-6 text-indigo-500" data-id="element-1298" data-mp="3p7md5ho16e4e" />;
  }
};
export default ContentPageHeader;